import { Dispatch, SetStateAction, useEffect, useRef, useState } from 'react';

import { analyticsMFAMethodSelected } from '../../api/services/Analytics';
import { resendMFA } from '../../api/services/Auth';
import { isMFASessionExpired } from '../mfaVerification';
import { MFACodeSentResponse, MFAMethod, MFAMethodOption, MFAResponse } from '../types';

import { useMFACooldowns } from './useMFACooldowns';

function isCodeSent(data: unknown): data is MFACodeSentResponse {
  if (!data || typeof data !== 'object' || !('mfa' in data)) {
    return false;
  }
  const mfa = data.mfa;

  return (
    !!mfa &&
    typeof mfa === 'object' &&
    'state' in mfa &&
    mfa.state === 'code_sent' &&
    'method' in mfa &&
    (mfa.method === 'email' || mfa.method === 'sms') &&
    'destination' in mfa &&
    typeof mfa.destination === 'string' &&
    mfa.destination.length > 0
  );
}

interface MFAFlowOptions {
  onSessionExpired?: () => void;
  onDeliverySuccess?: () => void;
  onReturnToChoice?: () => void;
}

export function useMFAFlow({ onSessionExpired, onDeliverySuccess, onReturnToChoice }: MFAFlowOptions = {}) {
  const [mfaStep, setMFAStep] = useState<MFAResponse | null>(null);
  const [userEmail, setUserEmail] = useState('');
  const [sendingMethod, setSendingMethod] = useState<MFAMethod | null>(null);
  const [isVerifying, setIsVerifying] = useState(false);
  const verifying = useRef(false);
  const [methodErrors, setMethodErrors] = useState<string[]>([]);
  const [availableMethods, setAvailableMethods] = useState<MFAMethodOption[]>([]);
  const methods = useRef<MFAMethodOption[]>([]);
  const unavailableMethods = useRef(new Set<MFAMethod>());
  const [codeSent, setCodeSent] = useState(false);
  const { canSend, recordSend, recordRateLimit, reset, methodCooldownSeconds, rateLimitCooldownSeconds } =
    useMFACooldowns();
  const sendCooldownSeconds =
    mfaStep?.mfa?.state === 'choose_method'
      ? rateLimitCooldownSeconds
      : methodCooldownSeconds[mfaStep?.mfa?.method || 'email'];

  const rememberMethods = (options: MFAMethodOption[]) => {
    methods.current = options;
    setAvailableMethods(options);
  };

  const controller = useRef(new AbortController());
  const step = useRef<MFAResponse | null>(null);
  const sending = useRef(false);

  useEffect(() => () => controller.current.abort(), []);

  const setStep = (value: MFAResponse | null) => {
    step.current = value;
    setMFAStep(value);
  };

  const beginLogin = (email: string) => {
    controller.current.abort();
    controller.current = new AbortController();
    sending.current = false;
    verifying.current = false;
    setIsVerifying(false);
    setSendingMethod(null);
    setMethodErrors([]);
    rememberMethods([]);
    unavailableMethods.current.clear();
    reset();
    setCodeSent(false);
    setStep(null);
    setUserEmail(email);

    return controller.current.signal;
  };

  const isCurrentAttempt = (signal: AbortSignal) => controller.current.signal === signal && !signal.aborted;

  const closeMFA = () => {
    controller.current.abort();
    sending.current = false;
    verifying.current = false;
    setIsVerifying(false);
    setStep(null);
    setSendingMethod(null);
    setMethodErrors([]);
    setUserEmail('');
    rememberMethods([]);
    unavailableMethods.current.clear();
    reset();
    setCodeSent(false);
  };

  const setShowMFAModal: Dispatch<SetStateAction<boolean>> = (value) => {
    const show = typeof value === 'function' ? value(!!step.current) : value;

    if (!show) {
      closeMFA();
    }
  };

  const sendCode = async (method?: MFAMethod, source: 'selection' | 'resend' = 'resend'): Promise<boolean> => {
    if (
      sending.current ||
      verifying.current ||
      unavailableMethods.current.has(method || 'email') ||
      !canSend(method || 'email')
    ) {
      return false;
    }

    const signal = controller.current.signal;

    if (!isCurrentAttempt(signal)) {
      return false;
    }
    sending.current = true;
    setSendingMethod(method || 'email');
    setCodeSent(false);
    setMethodErrors([]);

    try {
      if (source === 'selection' && method) {
        analyticsMFAMethodSelected(method);
      }
      const response = await resendMFA(method, signal);

      if (!isCurrentAttempt(signal)) {
        return false;
      }
      if (isMFASessionExpired(response)) {
        closeMFA();
        onSessionExpired?.();

        return false;
      }
      if (response.type === 'success' && (isCodeSent(response.data) || !method)) {
        if (isCodeSent(response.data)) {
          setStep(response.data);
        }

        recordSend(isCodeSent(response.data) ? response.data.mfa.method : method || 'email');
        setCodeSent(true);
        onDeliverySuccess?.();

        return true;
      }
      if (response.type === 'error' && response.status === 429) {
        // No retry delay is supplied by the API; this is only the existing UI send cooldown.
        recordRateLimit();
      }
      if (response.type === 'error' && response.status === 422 && method === 'sms') {
        unavailableMethods.current.add('sms');
        const remaining = methods.current.filter((option) => option.method !== 'sms');

        rememberMethods(remaining);
        if (remaining.length) {
          setStep({ mfa: { state: 'choose_method', methods: remaining } });
          onReturnToChoice?.();
        }
        setMethodErrors(
          response.error.errors.length
            ? response.error.errors
            : [
                remaining.length
                  ? 'SMS is currently unavailable. Choose email to receive your code.'
                  : 'SMS is currently unavailable. Please sign in again.',
              ]
        );

        return false;
      }
      setMethodErrors(
        response.type === 'error' && response.error.errors.length
          ? response.error.errors
          : ['Unable to send a verification code. Please try again.']
      );
    } catch {
      if (isCurrentAttempt(signal)) {
        setMethodErrors(['Unable to send a verification code. Please try again.']);
      }
    } finally {
      if (isCurrentAttempt(signal)) {
        sending.current = false;
        setSendingMethod(null);
      }
    }

    return false;
  };

  const selectMFAMethod = async (method: MFAMethod) => {
    const options = step.current?.mfa;

    if (options?.state === 'choose_method' && options.methods.some((item) => item.method === method)) {
      await sendCode(method, 'selection');
    }
  };

  const resendMFACode = async () => {
    const current = step.current;

    if (!current || current.mfa?.state === 'choose_method') {
      return false;
    }

    return sendCode(current.mfa?.method);
  };

  const openMFA = (value: MFAResponse | null) => {
    rememberMethods(value?.mfa?.state === 'choose_method' ? [...value.mfa.methods] : []);
    setStep(value);
    if (isCodeSent(value) || (value?.message === 'Provide OTP code.' && !value.mfa)) {
      recordSend(value.mfa?.method || 'email');
      setCodeSent(true);
    }
  };

  const activeMethod = mfaStep?.mfa?.state === 'code_sent' ? mfaStep.mfa.method : undefined;
  const canChooseMFAMethod = !!activeMethod && availableMethods.some((option) => option.method !== activeMethod);

  const returnToMFAMethodChoice = () => {
    const current = step.current?.mfa;

    if (
      sending.current ||
      verifying.current ||
      current?.state !== 'code_sent' ||
      !methods.current.some((option) => option.method !== current.method)
    ) {
      return;
    }
    setStep({ mfa: { state: 'choose_method', methods: methods.current } });
    setMethodErrors([]);
    onReturnToChoice?.();
  };

  const beginVerification = (code: string) => {
    const current = step.current;
    const signal = controller.current.signal;
    const hasDeliveredCode = isCodeSent(current) || (current?.message === 'Provide OTP code.' && !current.mfa);

    if (
      !hasDeliveredCode ||
      !/^\d{6}$/.test(code) ||
      sending.current ||
      verifying.current ||
      !isCurrentAttempt(signal)
    ) {
      return null;
    }
    verifying.current = true;
    setIsVerifying(true);
    setMethodErrors([]);

    return signal;
  };

  const finishVerification = (signal: AbortSignal) => {
    if (isCurrentAttempt(signal)) {
      verifying.current = false;
      setIsVerifying(false);
    }
  };

  return {
    mfaStep,
    isVerifying,
    beginVerification,
    finishVerification,
    userEmail,
    sendingMethod,
    methodErrors,
    selectMFAMethod,
    resendMFACode,
    showMFAModal: !!mfaStep,
    setShowMFAModal,
    beginLogin,
    isCurrentAttempt,
    openMFA,
    canChooseMFAMethod,
    isDeliveryUnavailable: !!activeMethod && unavailableMethods.current.has(activeMethod),
    returnToMFAMethodChoice,
    sendCooldownSeconds,
    methodCooldownSeconds,
    codeSent,
    getAttemptSignal: () => controller.current.signal,
  };
}
